import React, { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaDownload,
  FaEdit,
  FaFilter,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUpload,
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import ConfirmDialog from "../components/ConfirmDialog";
import { PermissionGuard } from "../components/PermissionGuard";
import CompactActionBar from "../components/CompactActionBar";
import SharedFilter from "../components/SharedFilter";
import { useAuth } from "../contexts/AuthContext";
import { usePageTitle } from "../hooks/usePageTitle";
import "../css/pages/Units.css";
import { projectService } from "../services/projectService";
import { CreateUnitData, Unit, unitService } from "../services/unitService";
import { formatCurrency } from "../utils/formatters";
import { formatMainDBAmount } from "../utils/formatAmount";

interface Project {
  id: number;
  name: string;
}

const Units: React.FC = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const navigate = useNavigate();
  const { user } = useAuth();
  
  usePageTitle();

  // Debug: Check user permissions
  console.log("🔍 Units Page - User State:", {
    user: user?.employeeName,
    permissions: user?.allPermissions,
    hasUnitCreate: user?.allPermissions?.includes("unit.create"),
    isSuperAdmin:
      user?.allPermissions?.includes("*") ||
      user?.allPermissions?.includes("superadmin"),
  });

  const [project, setProject] = useState<Project | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<Unit | null>(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100); // Changed default from 20 to 100

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [wingFilter, setWingFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [terraceStatusFilter, setTerraceStatusFilter] = useState("");
  const [sizeMinFilter, setSizeMinFilter] = useState("");
  const [sizeMaxFilter, setSizeMaxFilter] = useState("");
  
  // Applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    project: "",
    wing: "",
    unit: "",
    type: "",
    terraceStatus: "",
    sizeMin: "",
    sizeMax: "",
    status: "",
  });

  // Available filter options (fetched from API)
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);

  // Form data
  const [formData, setFormData] = useState<CreateUnitData>({
    unitType: "",
    unitNo: "",
    wing: "",
    size: "",
    currentStatus: "unsold",
    saleDeedAmount: undefined,
    extraWorkAmount: undefined,
    terraceStatus: "",
    terraceSize: "",
    extraSpace: "",
    extraSpaceAmount: undefined,
  });

  const unitTypes = [
    "SHOP",
    "1BHK",
    "2BHK",
    "3BHK",
    "4BHK",
    "5BHK",
    "PENTHOUSE",
  ];
  const statusOptions = ["unsold", "sold", "approval_pending", "hold"];
  const wings = ["A", "B", "C", "D", "E"];

  // Debounced search effect to prevent input focus loss
  useEffect(() => {
    if (projectId) {
      fetchProject();
      fetchAvailableFilters();
      fetchUnits();
    }
  }, [projectId, currentPage, statusFilter, appliedFilters, limit]);

  const fetchProject = async () => {
    try {
      const projectData = await projectService.getProject(parseInt(projectId!));
      setProject(projectData);
    } catch (error) {
      console.error("Error fetching project:", error);
    }
  };

  const fetchAvailableFilters = async () => {
    try {
      // Fetch all units to get unique types and statuses
      const response = await unitService.getUnits(parseInt(projectId!), {
        page: 1,
        limit: 500, // Reduced limit to avoid content length errors
      });
      
      // Extract unique unit types
      const typesSet = new Set(response.units.map(unit => unit.unitType));
      const types = Array.from(typesSet).filter(Boolean).sort();
      setAvailableTypes(types);
      
      // Extract unique statuses
      const statusesSet = new Set(response.units.map(unit => unit.currentStatus));
      const statuses = Array.from(statusesSet).filter(Boolean).sort();
      setAvailableStatuses(statuses);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchUnits = async () => {
    try {
      setLoading(true);
      
      // Build search term from applied filters
      const searchParts = [];
      if (appliedFilters.unit) searchParts.push(appliedFilters.unit);
      const searchTerm = searchParts.join(' ');
      
      const params = {
        page: currentPage,
        limit,
        ...(appliedFilters.status && { status: appliedFilters.status }),
        ...(appliedFilters.type && { type: appliedFilters.type }),
        ...(appliedFilters.terraceStatus && { terraceStatus: appliedFilters.terraceStatus }),
        ...(appliedFilters.sizeMin && { sizeMin: appliedFilters.sizeMin }),
        ...(appliedFilters.sizeMax && { sizeMax: appliedFilters.sizeMax }),
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await unitService.getUnits(parseInt(projectId!), params);
      // Sort units by Type -> Wing -> Unit No (numeric where possible)
      const sortUnits = (arr: Unit[]) => {
        return [...arr].sort((a, b) => {
          const typeCmp = (a.unitType || '').localeCompare(b.unitType || '');
          if (typeCmp !== 0) return typeCmp;
          const wingCmp = (a.wing || '').localeCompare(b.wing || '');
          if (wingCmp !== 0) return wingCmp;
          const aNum = parseInt((a.unitNo || '').replace(/[^0-9]/g, '') || '0', 10);
          const bNum = parseInt((b.unitNo || '').replace(/[^0-9]/g, '') || '0', 10);
          if (aNum !== bNum) return aNum - bNum;
          return (a.unitNo || '').localeCompare(b.unitNo || '');
        });
      };
      setUnits(sortUnits(response.units));
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error("Error fetching units:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Validation: Check for duplicate wing-unit combination
      if (formData.wing && formData.unitNo && !editingUnit) {
        const existingUnit = units.find(
          (unit) =>
            unit.wing === formData.wing && unit.unitNo === formData.unitNo
        );

        if (existingUnit) {
          alert(
            `A unit with wing "${formData.wing}" and unit number "${formData.unitNo}" already exists in this project.`
          );
          return;
        }
      }

      // For editing, check if the combination exists in other units (excluding current unit)
      if (formData.wing && formData.unitNo && editingUnit) {
        const existingUnit = units.find(
          (unit) =>
            unit.wing === formData.wing &&
            unit.unitNo === formData.unitNo &&
            unit.id !== editingUnit.id
        );

        if (existingUnit) {
          alert(
            `A unit with wing "${formData.wing}" and unit number "${formData.unitNo}" already exists in this project.`
          );
          return;
        }
      }

      if (editingUnit) {
        await unitService.updateUnit(editingUnit.id, formData);
      } else {
        await unitService.createUnit(parseInt(projectId!), formData);
      }

      setShowModal(false);
      setEditingUnit(null);
      resetForm();
      fetchUnits();
    } catch (error) {
      console.error("Error saving unit:", error);
    }
  };

  const handleEdit = (unit: Unit) => {
    if (projectId) {
      navigate(`/project/unit/edit?id=${unit.id}&projectId=${projectId}`);
    } else {
      navigate(`/unit/edit?id=${unit.id}`);
    }
  };

  const handleDelete = (unit: Unit) => {
    setUnitToDelete(unit);
    setShowDeleteDialog(true);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1); // Reset to first page when limit changes
  };

  const confirmDelete = async () => {
    if (!unitToDelete) return;

    try {
      await unitService.deleteUnit(unitToDelete.id);
      setShowDeleteDialog(false);
      setUnitToDelete(null);
      fetchUnits();
    } catch (error) {
      console.error("Error deleting unit:", error);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setUnitToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      unitType: "",
      unitNo: "",
      wing: "",
      size: "",
      currentStatus: "unsold",
      saleDeedAmount: undefined,
      extraWorkAmount: undefined,
      terraceStatus: "",
      terraceSize: "",
      extraSpace: "",
      extraSpaceAmount: undefined,
    });
  };

  /**
   * Parse unit number from combined format
   * Examples:
   * - "B-101" -> { wing: "B", unitNo: "101" }
   * - "1001" -> { wing: null, unitNo: "1001" }
   * - "203" -> { wing: null, unitNo: "203" }
   */
  const parseUnitNumber = (
    combined: string
  ): { wing: string | null; unitNo: string } => {
    if (!combined) return { wing: null, unitNo: "" };

    const trimmed = combined.toString().trim();

    // Check if format contains hyphen (e.g., "B-101")
    if (trimmed.includes("-")) {
      const parts = trimmed.split("-");
      if (parts.length === 2) {
        return {
          wing: parts[0].trim(),
          unitNo: parts[1].trim(),
        };
      }
    }

    // No hyphen, treat as unit number only
    return {
      wing: null,
      unitNo: trimmed,
    };
  };

  const openModal = () => {
    setEditingUnit(null);
    resetForm();
    setShowModal(true);
  };

  const downloadTemplate = async () => {
    try {
      const blob = await unitService.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "unit_upload_template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      // Accept CSV and Excel files
      const isCSV = fileType === "text/csv" || fileName.endsWith(".csv");
      const isExcel =
        fileType ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        fileType === "application/vnd.ms-excel" ||
        fileName.endsWith(".xlsx") ||
        fileName.endsWith(".xls");

      if (isCSV || isExcel) {
        setCsvFile(file);
      } else {
        alert("Please select a valid CSV or Excel file (.csv, .xlsx, .xls)");
      }
    }
  };

  const handleBulkUpload = async () => {
    if (!csvFile || !projectId) return;

    setIsUploading(true);
    try {
      const fileName = csvFile.name.toLowerCase();
      const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

      let processedUnits: CreateUnitData[] = [];

      if (isExcel) {
        // Handle Excel file
        const data = await csvFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON with header row
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        });

        if (jsonData.length < 2) {
          alert(
            "Excel file must contain at least a header row and one data row"
          );
          setIsUploading(false);
          return;
        }

        // Skip header row (index 0) and process data rows
        processedUnits = jsonData
          .slice(1)
          .map((row: any[]) => {
            const unit: any = {};

            // 🔍 ACTUAL EXCEL FORMAT:
            // Column 0: Project name
            // Column 1: Unit type
            // Column 2: Unit no
            // Column 3: Size
            // Column 4: Current status
            // Column 5: Sale deed amount
            // Column 6: Terrace status
            // Column 7: Terrace size

            // Column 1 - Unit Type
            if (row[1]) {
              unit.unitType = row[1].toString().trim();
            }

            // Column 2 - Unit Number (parse to get wing and unit number)
            if (row[2]) {
              const parsed = parseUnitNumber(row[2].toString());
              unit.wing = parsed.wing;
              unit.unitNo = parsed.unitNo;
            }

            // Column 3 - Size
            if (row[3]) {
              unit.size = row[3].toString().trim();
            }

            // Column 4 - Current Status
            if (row[4]) {
              const statusValue = row[4].toString().trim().toLowerCase();
              if (statusValue) {
                // Map Excel status to database values
                let mappedStatus = statusValue;
                if (statusValue === "sold") {
                  mappedStatus = "sold";
                } else if (statusValue === "available") {
                  mappedStatus = "unsold";
                } else {
                  mappedStatus = "unsold"; // default
                }
                unit.currentStatus = mappedStatus;
              } else {
                unit.currentStatus = "unsold";
              }
            } else {
              unit.currentStatus = "unsold";
            }

            // Column 5 - Sale Deed Amount
            if (row[5]) {
              const amount = parseFloat(
                row[5].toString().replace(/[^0-9.]/g, "")
              );
              if (!isNaN(amount)) {
                unit.saleDeedAmount = amount;
              }
            }

            // Column 6 - Terrace Status
            if (row[6]) {
              const terraceValue = row[6].toString().trim();
              if (terraceValue) {
                unit.terraceStatus = terraceValue;
              }
            }

            // Column 7 - Terrace Size
            if (row[7]) {
              const terraceSizeValue = row[7].toString().trim();
              if (terraceSizeValue) {
                unit.terraceSize = terraceSizeValue;
              }
            }

            return unit as CreateUnitData;
          })
          .filter((unit) => unit.unitNo && unit.unitType); // Filter out empty rows
      } else {
        // Handle CSV file (existing logic)
        const text = await csvFile.text();
        const lines = text
          .split("\n")
          .filter((line) => line.trim()) // Remove empty lines
          .filter((line) => !line.trim().startsWith("#")); // Remove comment lines

        const headers = lines[0].split(",").map((h) => h.trim());

        processedUnits = lines.slice(1).map((line) => {
          const values = line.split(",").map((v) => v.trim());
          const unit: any = {};

          headers.forEach((header, index) => {
            const value = values[index];

            // Handle empty values
            if (!value || value === "") {
              return;
            }

            switch (header) {
              case "unitNo":
                // Parse combined unit number
                const parsed = parseUnitNumber(value);
                unit.wing = parsed.wing;
                unit.unitNo = parsed.unitNo;
                break;
              case "wing":
                unit.wing = value;
                break;
              case "unitType":
              case "size":
              case "terraceStatus":
              case "terraceSize":
              case "extraSpace":
                unit[header] = value;
                break;
              case "currentStatus":
                // Handle status mapping for backward compatibility
                let mappedStatus = value.toLowerCase();
                if (mappedStatus === "available") {
                  mappedStatus = "unsold";
                } else if (mappedStatus === "sold") {
                  mappedStatus = "sold";
                } else if (mappedStatus === "pending") {
                  mappedStatus = "approval_pending";
                } else if (
                  !["unsold", "sold", "approval_pending", "hold"].includes(
                    mappedStatus
                  )
                ) {
                  // Default to unsold if unrecognized status
                  mappedStatus = "unsold";
                }
                unit[header] = mappedStatus;
                break;
              case "saleDeedAmount":
              case "extraWorkAmount":
              case "extraSpaceAmount":
                unit[header] = value ? Number(value) : undefined;
                break;
              // Handle legacy CSV field mappings (for backward compatibility)
              case "totalAmount":
                unit["saleDeedAmount"] = value ? Number(value) : undefined;
                break;
              case "paidAmount":
                unit["extraWorkAmount"] = value ? Number(value) : undefined;
                break;
              case "pendingAmount":
                unit["extraSpaceAmount"] = value ? Number(value) : undefined;
                break;
              case "terrace":
                unit["terraceStatus"] = value;
                break;
              case "sizeUnit":
                // Append size unit to size if exists
                if (unit.size && value) {
                  unit.size = `${unit.size} ${value}`;
                }
                break;
              case "terraceSizeUnit":
                // Append terrace size unit to terrace size if exists
                if (unit.terraceSize && value) {
                  unit.terraceSize = `${unit.terraceSize} ${value}`;
                }
                break;
              case "parkingSlots":
                unit["extraSpace"] = value
                  ? `${value} parking slots`
                  : undefined;
                break;
              case "floor":
                // Could be added to extraSpace or ignored for now
                if (unit.extraSpace) {
                  unit.extraSpace = `${unit.extraSpace}, Floor ${value}`;
                } else {
                  unit.extraSpace = `Floor ${value}`;
                }
                break;
            }
          });

          return unit as CreateUnitData;
        });
      }

      console.log("📊 Processed units:", processedUnits.length);
      console.log("🔍 First unit sample:", processedUnits[0]);

      // Validation 1: Check for duplicates within the uploaded data
      const uploadDuplicates = new Map<string, number[]>();
      processedUnits.forEach((unit, index) => {
        const key = unit.wing ? `${unit.wing}-${unit.unitNo}` : unit.unitNo;
        if (!uploadDuplicates.has(key)) {
          uploadDuplicates.set(key, []);
        }
        uploadDuplicates.get(key)!.push(index + 2); // +2 because we skip header and arrays are 0-indexed
      });

      const uploadDuplicateErrors: string[] = [];
      uploadDuplicates.forEach((rows, combination) => {
        if (rows.length > 1) {
          uploadDuplicateErrors.push(
            `Unit "${combination}" appears in rows: ${rows.join(", ")}`
          );
        }
      });

      if (uploadDuplicateErrors.length > 0) {
        alert(
          `Duplicate units found in file:\n\n${uploadDuplicateErrors.join(
            "\n"
          )}\n\nPlease fix the duplicates and try again.`
        );
        setIsUploading(false);
        return;
      }

      // Validation 2: Check against existing units in database
      const allExistingUnits = await unitService.getUnits(parseInt(projectId), {
        page: 1,
        limit: 500,
      });
      const existingCombinations = new Set(
        allExistingUnits.units.map((unit) =>
          unit.wing ? `${unit.wing}-${unit.unitNo}` : unit.unitNo
        )
      );

      const duplicateWithExisting: string[] = [];
      processedUnits.forEach((unit) => {
        const combination = unit.wing
          ? `${unit.wing}-${unit.unitNo}`
          : unit.unitNo;
        if (existingCombinations.has(combination)) {
          duplicateWithExisting.push(combination);
        }
      });

      if (duplicateWithExisting.length > 0) {
        alert(
          `The following units already exist in the database:\n\n${duplicateWithExisting.join(
            "\n"
          )}\n\nPlease remove these from your file and try again.`
        );
        setIsUploading(false);
        return;
      }

      // If all validations pass, proceed with upload
      await unitService.bulkUploadUnits(parseInt(projectId), processedUnits);
      setShowBulkUpload(false);
      setCsvFile(null);
      fetchUnits();
      alert(`Successfully uploaded ${processedUnits.length} units!`);
    } catch (error) {
      console.error("Error uploading units:", error);
      alert(
        "Error uploading units. Please check the file format and try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const clearAllFilters = () => {
    setProjectFilter("");
    setWingFilter("");
    setUnitFilter("");
    setTypeFilter("");
    setTerraceStatusFilter("");
    setSizeMinFilter("");
    setSizeMaxFilter("");
    setStatusFilter("");
    setAppliedFilters({
      project: "",
      wing: "",
      unit: "",
      type: "",
      terraceStatus: "",
      sizeMin: "",
      sizeMax: "",
      status: "",
    });
    setCurrentPage(1);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "sold":
        return "status-sold";
      case "unsold":
        return "status-unsold";
      case "approval_pending":
        return "status-pending";
      case "hold":
        return "status-hold";
      default:
        return "status-default";
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      ...appliedFilters,
      unit: unitFilter,
      type: typeFilter,
      terraceStatus: terraceStatusFilter,
      sizeMin: sizeMinFilter,
      sizeMax: sizeMaxFilter,
    });
    setCurrentPage(1);
  };

  if (loading && units.length === 0) {
    return <div className="loading">Loading units...</div>;
  }

  return (
    <div className="page-container" style={{ background: '#fff' }}>
      {/* Compact Action Bar */}
      <CompactActionBar
        leftActions={
          <>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => navigate("/projects")}
            >
              <FaArrowLeft /> Back to Projects
            </button>
            
            <button 
              className="btn btn-secondary"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaSearch /> Filters
            </button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setAppliedFilters({...appliedFilters, status: e.target.value});
                  setCurrentPage(1);
                }}
                className="form-select"
                style={{ padding: '6px 12px', minWidth: '130px' }}
              >
                <option value="">All Status</option>
                {availableStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Show:</span>
              <select
                value={limit}
                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                className="form-select"
                style={{ padding: '6px 12px', minWidth: '100px' }}
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={300}>300</option>
                <option value={400}>400</option>
                <option value={500}>500</option>
              </select>
            </div>
          </>
        }
        rightActions={
          <>
            <PermissionGuard permission="unit.create">
              <button
                className="btn btn-primary"
                onClick={() =>
                  projectId
                    ? navigate(`/project/unit/add?projectId=${projectId}`)
                    : navigate("/unit/add")
                }
              >
                <FaPlus /> Add Unit
              </button>
            </PermissionGuard>
            <button className="btn btn-secondary" onClick={downloadTemplate}>
              <FaDownload /> Download Template
            </button>
            <PermissionGuard permission="unit.create">
              <button
                className="btn btn-secondary"
                onClick={() => setShowBulkUpload(true)}
              >
                <FaUpload /> Bulk Upload
              </button>
            </PermissionGuard>
          </>
        }
      />

      {/* Custom Filter Component for Units */}
      {showFilters && (
        <div style={{ 
          background: '#fff', 
          padding: '20px', 
          marginBottom: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyFilters(); } }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Unit Number
              </label>
              <input
                type="text"
                placeholder="e.g., 301, G-401"
                value={unitFilter}
                onChange={(e) => setUnitFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Type
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">All Types</option>
                {availableTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Terrace Status
              </label>
              <select
                value={terraceStatusFilter}
                onChange={(e) => setTerraceStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Size (Min)
              </label>
              <input
                type="number"
                placeholder="Min size"
                value={sizeMinFilter}
                onChange={(e) => setSizeMinFilter(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Size (Max)
              </label>
              <input
                type="number"
                placeholder="Max size"
                value={sizeMaxFilter}
                onChange={(e) => setSizeMaxFilter(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={clearAllFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: '#fff',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Clear All
            </button>
            <button
              onClick={handleApplyFilters}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                background: '#4f46e5',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Units Table */}
      <div style={{ background: '#fff', margin: 0, padding: 0 }}>
        <table className="standard-table" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Type</th>
              <th>Wing</th>
              <th>Unit No</th>
              <th>Size</th>
              <th>Status</th>
              <th className="text-right">Sale Amount</th>
              <th>Terrace Status</th>
              <th>Terrace Size</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {units.length === 0 ? (
              <tr>
                <td colSpan={10} className="no-data">
                  No units found
                </td>
              </tr>
            ) : (
              units.map((unit, index) => (
                <tr key={unit.id}>
                  <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                    {units.length - index}
                  </td>
                  <td className="unit-type">{unit.unitType}</td>
                  <td className="wing">{unit.wing}</td>
                  <td className="unit-no">{unit.unitNo}</td>
                  <td className="size">{unit.size}</td>
                  <td>
                    <span
                      className={`status-badge ${getStatusBadgeClass(
                        unit.currentStatus
                      )}`}
                    >
                      {unit.currentStatus.replace("_", " ").toUpperCase()}
                    </span>
                  </td>
                  <td className="amount text-right">
                    {formatMainDBAmount(unit.saleDeedAmount)}
                  </td>
                  <td>{unit.terraceStatus || "-"}</td>
                  <td>{unit.terraceSize || "-"}</td>
                  <td className="actions">
                    <PermissionGuard permission="unit.edit">
                      <button
                        className="action-btn edit"
                        onClick={() => handleEdit(unit)}
                        title="Edit Unit"
                      >
                        <FaEdit />
                      </button>
                    </PermissionGuard>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Info */}
      <div className="pagination-info">
        <span>
          Showing {(currentPage - 1) * limit + 1} to{" "}
          {Math.min(currentPage * limit, total)} of {total} units
        </span>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>

          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  className={`page-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h2>{editingUnit ? "Edit Unit" : "Add New Unit"}</h2>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="unitNo">Unit Number *</label>
                  <input
                    type="text"
                    id="unitNo"
                    value={formData.unitNo}
                    onChange={(e) =>
                      setFormData({ ...formData, unitNo: e.target.value })
                    }
                    placeholder="A-101"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="unitType">Unit Type *</label>
                  <select
                    id="unitType"
                    value={formData.unitType}
                    onChange={(e) =>
                      setFormData({ ...formData, unitType: e.target.value })
                    }
                    required
                  >
                    <option value="">Select Type</option>
                    {unitTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="wing">Wing *</label>
                  <select
                    id="wing"
                    value={formData.wing}
                    onChange={(e) =>
                      setFormData({ ...formData, wing: e.target.value })
                    }
                    required
                  >
                    <option value="">Select Wing</option>
                    {wings.map((wing) => (
                      <option key={wing} value={wing}>
                        {wing}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="size">Size *</label>
                  <input
                    type="text"
                    id="size"
                    value={formData.size}
                    onChange={(e) =>
                      setFormData({ ...formData, size: e.target.value })
                    }
                    placeholder="330 Sq Yards"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="currentStatus">Status *</label>
                  <select
                    id="currentStatus"
                    value={formData.currentStatus}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        currentStatus: e.target.value,
                      })
                    }
                    required
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status.replace("_", " ").toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="saleDeedAmount">Sale Deed Amount</label>
                  <input
                    type="number"
                    id="saleDeedAmount"
                    step="1"
                    value={formData.saleDeedAmount || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        saleDeedAmount: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                      })
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="Enter sale deed amount (no decimals)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="extraWorkAmount">Extra Work Amount</label>
                  <input
                    type="number"
                    id="extraWorkAmount"
                    step="0.01"
                    value={formData.extraWorkAmount || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        extraWorkAmount: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                      })
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="Enter extra work amount (2 decimals)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="terraceStatus">Terrace Status</label>
                  <select
                    id="terraceStatus"
                    value={formData.terraceStatus}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        terraceStatus: e.target.value,
                      })
                    }
                  >
                    <option value="">No Terrace</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="terraceSize">Terrace Size</label>
                  <input
                    type="text"
                    id="terraceSize"
                    value={formData.terraceSize}
                    onChange={(e) =>
                      setFormData({ ...formData, terraceSize: e.target.value })
                    }
                    placeholder="100 Sq Ft"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="extraSpace">Extra Space</label>
                  <input
                    type="text"
                    id="extraSpace"
                    value={formData.extraSpace}
                    onChange={(e) =>
                      setFormData({ ...formData, extraSpace: e.target.value })
                    }
                    placeholder="Parking, Storage, etc."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="extraSpaceAmount">Extra Space Amount</label>
                  <input
                    type="number"
                    id="extraSpaceAmount"
                    step="0.01"
                    value={formData.extraSpaceAmount || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        extraSpaceAmount: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                      })
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="Enter extra space amount (2 decimals)"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingUnit ? "Update" : "Create"} Unit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Bulk Upload Units</h2>
              <button
                className="close-btn"
                onClick={() => setShowBulkUpload(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="upload-section">
                <p>
                  Upload units from a CSV or Excel file. First download the
                  template to see the required format.
                </p>

                <div className="upload-steps">
                  <h4>Excel File Format (Recommended)</h4>
                  <ol>
                    <li>
                      <strong>Column A:</strong> Project Name (skip - not
                      needed)
                    </li>
                    <li>
                      <strong>Column B:</strong> Unit Type (e.g., 2BHK, 3BHK,
                      SHOP)
                    </li>
                    <li>
                      <strong>Column C:</strong> Unit No (e.g., B-101 or just
                      1001)
                    </li>
                    <li>
                      <strong>Column D:</strong> Size (e.g., 1200 sqft)
                    </li>
                    <li>
                      <strong>Column E:</strong> Sale Deed Amount
                    </li>
                    <li>
                      <strong>Column F:</strong> Terrace Status (Yes/No)
                    </li>
                    <li>
                      <strong>Column G:</strong> Terrace Size (e.g., 200 sqft)
                    </li>
                  </ol>

                  <h4>Unit Number Format</h4>
                  <ul>
                    <li>
                      <strong>With Wing:</strong> B-101 → Wing: B, Unit: 101
                    </li>
                    <li>
                      <strong>Without Wing:</strong> 1001 → Wing: (empty), Unit:
                      1001
                    </li>
                  </ul>

                  <h4>CSV Template</h4>
                  <p>
                    Alternatively, download the CSV template for the legacy
                    format
                  </p>
                </div>

                <div className="file-upload">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    id="csvFile"
                  />
                  <label htmlFor="csvFile" className="file-upload-label">
                    {csvFile ? csvFile.name : "Choose CSV or Excel file"}
                  </label>
                </div>

                {csvFile && (
                  <div className="file-info">
                    <p>Selected: {csvFile.name}</p>
                    <p>Size: {(csvFile.size / 1024).toFixed(2)} KB</p>
                    <p>
                      Type:{" "}
                      {csvFile.name.toLowerCase().endsWith(".xlsx") ||
                      csvFile.name.toLowerCase().endsWith(".xls")
                        ? "Excel"
                        : "CSV"}
                    </p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={downloadTemplate}
                >
                  <FaDownload /> Download CSV Template
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowBulkUpload(false)}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleBulkUpload}
                  disabled={!csvFile || isUploading}
                >
                  {isUploading ? "Uploading..." : "Upload Units"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Unit"
        message={`Are you sure you want to delete unit "${unitToDelete?.unitNo}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default Units;
